"use strict";
var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
var __importStar = (this && this.__importStar) || function (mod) {
    if (mod && mod.__esModule) return mod;
    var result = {};
    if (mod != null) for (var k in mod) if (Object.hasOwnProperty.call(mod, k)) result[k] = mod[k];
    result["default"] = mod;
    return result;
};
Object.defineProperty(exports, "__esModule", { value: true });
var change_emitter_1 = require("change-emitter");
var lodash_isequal_1 = __importDefault(require("lodash.isequal"));
var symbol_observable_1 = __importDefault(require("symbol-observable"));
var zen_observable_1 = __importDefault(require("zen-observable"));
var trie_1 = __importDefault(require("./trie"));
var utils_1 = require("./utils");
var optional_1 = __importDefault(require("./optional"));
var memory_store_1 = __importDefault(require("./memory-store"));
var exponent_refresh_error_policy_1 = __importDefault(require("./exponent-refresh-error-policy"));
var StoredKeyUtils = __importStar(require("./stored-key-utils"));
var split_join_1 = require("./split-join");
var allowedKeyStates = new Set(["requested", "cached", "missing"]);
var TweekRepository = (function () {
    function TweekRepository(_a) {
        var client = _a.client, refreshDelay = _a.refreshDelay, _b = _a.refreshErrorPolicy, refreshErrorPolicy = _b === void 0 ? exponent_refresh_error_policy_1.default() : _b, _c = _a.context, context = _c === void 0 ? {} : _c;
        this._emitter = change_emitter_1.createChangeEmitter();
        this._cache = new trie_1.default(split_join_1.TweekKeySplitJoin);
        this._isDirty = false;
        this._retryCount = 0;
        this._refreshInProgress = false;
        this._refreshPromise = Promise.resolve();
        this.listen = this._emitter.listen;
        this._client = client;
        this._store = new memory_store_1.default();
        this._refreshDelay = refreshDelay || 30;
        this._refreshErrorPolicy = refreshErrorPolicy;
        this._context = context;
    }
    TweekRepository.prototype.updateContext = function (valueOrMapper) {
        var _this = this;
        if (typeof valueOrMapper === 'function') {
            valueOrMapper = valueOrMapper(this._context);
            if (!valueOrMapper) {
                return;
            }
        }
        this._waitRefreshCycle().then(function () {
            _this._context = valueOrMapper;
            _this.expire();
        });
    };
    TweekRepository.prototype.addKeys = function (keys) {
        var _this = this;
        var updatedKeys = [];
        Object.entries(keys).forEach(function (_a) {
            var key = _a[0], value = _a[1];
            if (_this._updateNode(key, value)) {
                updatedKeys.push(key);
            }
        });
        this._emit(updatedKeys);
    };
    TweekRepository.prototype.useStore = function (store) {
        var _this = this;
        this._store = store;
        return this._store.load().then(function (keys) {
            keys = keys || {};
            var entries = Object.entries(keys);
            for (var _i = 0, entries_1 = entries; _i < entries_1.length; _i++) {
                var _a = entries_1[_i], key = _a[0], value = _a[1];
                if (!allowedKeyStates.has(value.state) || utils_1.isScanKey(key) !== value.isScan) {
                    throw new Error('stored cache is corrupted. not loading');
                }
            }
            var updatedKeys = [];
            for (var _b = 0, entries_2 = entries; _b < entries_2.length; _b++) {
                var _c = entries_2[_b], key = _c[0], value = _c[1];
                if (value.expiration) {
                    _this._isDirty = true;
                    _this._checkRefresh();
                    value = StoredKeyUtils.expire(value);
                }
                if (!value.isScan && value.state !== "requested") {
                    var cached = _this._cache.get(key);
                    if (!cached || !lodash_isequal_1.default(cached.value, value.value)) {
                        updatedKeys.push(key);
                    }
                }
                _this._cache.set(key, value);
            }
            _this._emit(updatedKeys);
        });
    };
    TweekRepository.prototype.prepare = function (key) {
        var node = this._cache.get(key);
        if (!node) {
            var isScan = utils_1.isScanKey(key);
            this._cache.set(key, StoredKeyUtils.request(isScan));
            this._isDirty = true;
            this._checkRefresh();
        }
    };
    TweekRepository.prototype.get = function (key) {
        var _this = this;
        var cached = this.getCached(key);
        if (!cached) {
            this.prepare(key);
        }
        else if (cached.state !== "requested") {
            return Promise.resolve(utils_1.getValueOrOptional(cached));
        }
        return new Promise(function (resolve, reject) {
            var unlisten = _this.listen(function (updatedKeys) {
                if (updatedKeys.has(key)) {
                    unlisten();
                    var cached_1 = _this.getCached(key);
                    if (!cached_1 || cached_1.state === "requested") {
                        reject(new Error('repository state is corrupted'));
                    }
                    else {
                        resolve(utils_1.getValueOrOptional(cached_1));
                    }
                }
            });
        });
    };
    TweekRepository.prototype.getValue = function (key) {
        var _this = this;
        var cached = this.getCached(key);
        if (!cached) {
            this.prepare(key);
        }
        else if (cached.state !== "requested") {
            return Promise.resolve(cached.value);
        }
        return new Promise(function (resolve, reject) {
            var unlisten = _this.listen(function (updatedKeys) {
                if (updatedKeys.has(key)) {
                    unlisten();
                    var cached_2 = _this.getCached(key);
                    if (!cached_2 || cached_2.state === "requested") {
                        reject(new Error('repository state is corrupted'));
                    }
                    else {
                        resolve(cached_2.value);
                    }
                }
            });
        });
    };
    TweekRepository.prototype.refresh = function (keysToRefresh) {
        this.expire(keysToRefresh);
    };
    TweekRepository.prototype.expire = function (keysToRefresh) {
        if (keysToRefresh === void 0) { keysToRefresh = Object.keys(this._cache.list()); }
        for (var _i = 0, keysToRefresh_1 = keysToRefresh; _i < keysToRefresh_1.length; _i++) {
            var key = keysToRefresh_1[_i];
            var node = this._cache.get(key);
            if (!node) {
                this.prepare(key);
                continue;
            }
            if (node.expiration !== "refreshing") {
                this._isDirty = true;
                if (node.expiration !== "expired") {
                    this._cache.set(key, StoredKeyUtils.expire(node));
                }
            }
        }
        this._checkRefresh();
    };
    TweekRepository.prototype.observe = function (key) {
        var _this = this;
        var isScan = utils_1.isScanKey(key);
        return new zen_observable_1.default(function (observer) {
            var onKey = function () {
                var cached = _this.getCached(key);
                if (!cached) {
                    _this.prepare(key);
                    return;
                }
                if (isScan !== cached.isScan) {
                    observer.error(new Error('corrupted cache'));
                    return;
                }
                if (cached.state === "cached") {
                    observer.next(isScan ? cached.value : optional_1.default.some(cached.value));
                    return;
                }
                if (cached.state === "missing") {
                    observer.next(optional_1.default.none());
                }
            };
            onKey();
            return _this.listen(function (updatedKeys) {
                if (!updatedKeys.has(key)) {
                    return;
                }
                onKey();
            });
        });
    };
    TweekRepository.prototype.observeValue = function (key) {
        var _this = this;
        return new zen_observable_1.default(function (observer) {
            var onKey = function () {
                var cached = _this.getCached(key);
                if (!cached) {
                    _this.prepare(key);
                    return;
                }
                if (cached.state === "requested") {
                    return;
                }
                observer.next(cached.value);
            };
            onKey();
            return _this.listen(function (updatedKeys) {
                if (!updatedKeys.has(key)) {
                    return;
                }
                onKey();
            });
        });
    };
    TweekRepository.prototype.getCached = function (key) {
        var node = this._cache.get(key);
        if (!node) {
            return undefined;
        }
        var state = node.state, isScan = node.isScan, value = node.value;
        if (state === "cached" && isScan) {
            var prefix = utils_1.getKeyPrefix(key);
            var relative = Object.values(this._cache.list(prefix));
            if (relative.some(function (v) { return !v.isScan && v.state === "requested"; })) {
                state = "requested";
                value = undefined;
            }
            else {
                value = this._extractScanResult(key);
            }
        }
        return {
            state: state,
            isScan: isScan,
            value: value,
        };
    };
    TweekRepository.prototype[symbol_observable_1.default] = function () {
        return this.observeValue('_');
    };
    TweekRepository.prototype._waitRefreshCycle = function () {
        if (!this._refreshInProgress)
            return Promise.resolve();
        return this._refreshPromise;
    };
    TweekRepository.prototype._checkRefresh = function () {
        var _this = this;
        this._refreshPromise = this._refreshInProgress
            ? this._refreshPromise.then(function () {
                if (!_this._refreshInProgress)
                    return _this._rollRefresh();
                return;
            })
            : this._rollRefresh();
    };
    TweekRepository.prototype._rollRefresh = function () {
        var _this = this;
        if (!this._isDirty) {
            this._refreshInProgress = false;
            return Promise.resolve();
        }
        this._refreshInProgress = true;
        return (this._retryCount === 0 ? utils_1.delay(this._refreshDelay) : Promise.resolve())
            .then(function () { return _this._refreshKeys(); })
            .then(function () {
            _this._refreshInProgress = false;
            _this._retryCount = 0;
        })
            .catch(function (ex) {
            _this._refreshErrorPolicy(utils_1.once(function () {
                _this._rollRefresh();
            }), ++_this._retryCount, ex);
        });
    };
    TweekRepository.prototype._refreshKeys = function () {
        var _this = this;
        if (!this._isDirty)
            return Promise.resolve();
        this._isDirty = false;
        var expiredKeys = Object.entries(this._cache.list()).filter(function (_a) {
            var _ = _a[0], valueNode = _a[1];
            return valueNode.state === "requested" || valueNode.expiration === "expired";
        });
        if (expiredKeys.length === 0)
            return Promise.resolve();
        expiredKeys.forEach(function (_a) {
            var key = _a[0], valueNode = _a[1];
            return _this._cache.set(key, StoredKeyUtils.refresh(valueNode));
        });
        var keysToRefresh = expiredKeys.map(function (_a) {
            var key = _a[0];
            return key;
        });
        var fetchConfig = {
            flatten: true,
            casing: 'snake',
            context: this._context,
            include: keysToRefresh,
        };
        return this._client
            .fetch('_', fetchConfig)
            .catch(function (err) {
            expiredKeys.forEach(function (_a) {
                var key = _a[0], valueNode = _a[1];
                return _this._cache.set(key, StoredKeyUtils.expire(valueNode));
            });
            _this._isDirty = true;
            throw err;
        })
            .then(function (keyValues) { return _this._updateTrieKeys(keysToRefresh, keyValues); })
            .then(function (updatedKeys) { return _this._store.save(_this._cache.list()).then(function () { return _this._emit(updatedKeys); }); });
    };
    TweekRepository.prototype._updateTrieKeys = function (keys, keyValues) {
        var valuesTrie;
        var updatedKeys = [];
        for (var _i = 0, keys_1 = keys; _i < keys_1.length; _i++) {
            var keyToUpdate = keys_1[_i];
            var isScan = utils_1.isScanKey(keyToUpdate);
            if (isScan) {
                if (!valuesTrie) {
                    valuesTrie = trie_1.default.from(split_join_1.TweekKeySplitJoin, keyValues);
                }
                Array.prototype.push.apply(updatedKeys, this._updateTrieScanKey(keyToUpdate, keyValues, valuesTrie));
            }
            else if (this._updateNode(keyToUpdate, keyValues[keyToUpdate])) {
                updatedKeys.push(keyToUpdate);
            }
        }
        return updatedKeys;
    };
    TweekRepository.prototype._updateTrieScanKey = function (key, keyValues, valuesTrie) {
        var _this = this;
        this._cache.set(key, StoredKeyUtils.cached(true));
        var prefix = utils_1.getKeyPrefix(key);
        var updatedKeys = [];
        var keysToUpdate = utils_1.distinct(this._cache.listEntries(prefix).concat(valuesTrie.listEntries(prefix)));
        keysToUpdate.forEach(function (subKey) {
            if (utils_1.isScanKey(subKey)) {
                _this._cache.set(subKey, StoredKeyUtils.cached(true));
                var fullPrefix = utils_1.getKeyPrefix(subKey);
                var entries = valuesTrie.listEntries(fullPrefix);
                _this._setScanNodes(entries);
            }
            else if (_this._updateNode(subKey, keyValues[subKey])) {
                updatedKeys.push(subKey);
            }
        });
        return updatedKeys;
    };
    TweekRepository.prototype._setScanNodes = function (keys) {
        var _this = this;
        utils_1.distinct(utils_1.flatMap(keys, function (key) { return utils_1.getAllPrefixes(key); }))
            .map(function (path) { return path + "/_"; })
            .filter(function (path) { return !_this._cache.get(path); })
            .forEach(function (key) { return _this._cache.set(key, StoredKeyUtils.cached(true)); });
    };
    TweekRepository.prototype._updateNode = function (key, value) {
        var cached = this._cache.get(key);
        var updated = !cached || cached.state === "requested" || !lodash_isequal_1.default(cached.value, value);
        if (value === undefined) {
            this._cache.set(key, StoredKeyUtils.missing());
        }
        else {
            this._cache.set(key, StoredKeyUtils.cached(false, value));
        }
        return updated;
    };
    TweekRepository.prototype._extractScanResult = function (key) {
        var prefix = utils_1.getKeyPrefix(key);
        var result = {};
        for (var _i = 0, _a = Object.entries(this._cache.listRelative(prefix)); _i < _a.length; _i++) {
            var _b = _a[_i], key_1 = _b[0], valueNode = _b[1];
            if (valueNode.isScan || valueNode.state !== "cached") {
                continue;
            }
            var _c = utils_1.partitionByIndex(split_join_1.TweekKeySplitJoin.split(key_1).map(utils_1.snakeToCamelCase), -1), fragments = _c[0], name_1 = _c[1][0];
            var node = result;
            for (var _d = 0, fragments_1 = fragments; _d < fragments_1.length; _d++) {
                var fragment = fragments_1[_d];
                if (!(fragment in node)) {
                    node[fragment] = {};
                }
                node = node[fragment];
            }
            node[name_1] = valueNode.value;
        }
        return result;
    };
    TweekRepository.prototype._emit = function (keys) {
        if (!keys.length) {
            return;
        }
        var relative = utils_1.flatMap(keys, function (k) { return utils_1.getAllPrefixes(k).map(function (scan) { return scan + "/_"; }); });
        var affectedKeys = new Set(keys.concat(relative));
        affectedKeys.add('_');
        this._emitter.emit(affectedKeys);
    };
    __decorate([
        utils_1.deprecated('getValue')
    ], TweekRepository.prototype, "get", null);
    __decorate([
        utils_1.deprecated('expire')
    ], TweekRepository.prototype, "refresh", null);
    __decorate([
        utils_1.deprecated('observeValue')
    ], TweekRepository.prototype, "observe", null);
    return TweekRepository;
}());
exports.TweekRepository = TweekRepository;
//# sourceMappingURL=tweek-repository.js.map